import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { announcementApi, Announcement } from '@/api/announcements'
import { inquiryApi, type Inquiry as InquiryType, type InquiryDetail } from '@/api/inquiry'
import { TiptapViewer } from '@/components/editor/TiptapViewer'
import { Dialog, DialogContent } from '@quebon/ui/components/dialog'
import {
  Headphones,
  MessageSquare,
  Bell,
  Plus,
  Pin,
  Calendar,
  User,
  Eye,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Paperclip,
  FileIcon,
  Download,
} from 'lucide-react'
import { clsx } from 'clsx'
import { toast } from 'sonner'

const formatDateDot = (dateString: string) => {
  const [datePart] = dateString.split('T')
  return datePart.replace(/-/g, '.')
}

const formatDateTimeDot = (dateString: string) => {
  const [datePart, timePart] = dateString.split('T')
  return `${datePart.replace(/-/g, '.')} ${timePart.substring(0, 5)}`
}

// ============================================================================
// Sub Components
// ============================================================================

interface InquiryCardProps {
  inquiry: InquiryType
  onClick: () => void
}

function InquiryCard({ inquiry, onClick }: InquiryCardProps) {
  const { t } = useTranslation('learner')

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      video_error: t('support.categoryVideoError'),
      quiz_error: t('support.categoryQuizError'),
      other_error: t('support.categoryOtherError'),
      learning: t('support.categoryLearning'),
      usage: t('support.categoryUsage'),
    }
    return labels[category] || category
  }

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; className: string }> = {
      pending: { label: t('support.pendingStatus'), className: 'bg-orange-100 text-orange-700' },
      answered: { label: t('support.answeredStatus'), className: 'bg-green-100 text-green-700' },
      closed: { label: t('support.closedStatus'), className: 'bg-gray-100 text-gray-700' },
    }
    return configs[status] || { label: status, className: 'bg-gray-100 text-gray-700' }
  }

  const statusConfig = getStatusConfig(inquiry.status)

  return (
    <div
      onClick={onClick}
      className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm transition-shadow cursor-pointer hover:shadow-md"
    >
      <div className="flex gap-4 justify-between items-start">
        <div className="flex-1 min-w-0">
          <div className="flex gap-2 items-center mb-2">
            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusConfig.className}`}>
              {statusConfig.label}
            </span>
            <span className="inline-flex px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
              {getCategoryLabel(inquiry.category)}
            </span>
            {inquiry.replyCount > 0 && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                <MessageSquare className="mr-1 w-3 h-3" />
                {inquiry.replyCount}
              </span>
            )}
          </div>
          <h3 className="mb-1 text-lg font-semibold text-gray-900 truncate">
            {inquiry.title}
          </h3>
        </div>
        <div className="flex flex-shrink-0 gap-1 items-center text-xs text-gray-500">
          <Calendar className="w-3 h-3" />
          {formatDateDot(inquiry.createdAt)}
        </div>
      </div>
    </div>
  )
}

interface InquiryDetailModalProps {
  inquiryId: number | null
  onClose: () => void
}

function InquiryDetailModal({ inquiryId, onClose }: InquiryDetailModalProps) {
  const { t } = useTranslation('learner')
  const queryClient = useQueryClient()
  const [replyContent, setReplyContent] = useState('')

  const { data: inquiry, isLoading, error } = useQuery({
    queryKey: ['inquiry', inquiryId],
    queryFn: () => inquiryApi.getById(inquiryId!),
    enabled: !!inquiryId,
  })

  const replyMutation = useMutation({
    mutationFn: (content: string) => inquiryApi.createReply(inquiryId!, content),
    onSuccess: () => {
      setReplyContent('')
      queryClient.invalidateQueries({ queryKey: ['inquiry', inquiryId] })
      queryClient.invalidateQueries({ queryKey: ['my-inquiries'] })
    },
    onError: () => {
      toast.error(t('support.additionalFailed'))
    },
  })

  const handleReplySubmit = () => {
    if (!replyContent.trim()) {
      toast.warning(t('support.enterContent'))
      return
    }
    replyMutation.mutate(replyContent)
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      video_error: t('support.categoryVideoError'),
      quiz_error: t('support.categoryQuizError'),
      other_error: t('support.categoryOtherError'),
      learning: t('support.categoryLearning'),
      usage: t('support.categoryUsage'),
    }
    return labels[category] || category
  }

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; className: string }> = {
      pending: { label: t('support.pendingStatus'), className: 'bg-orange-100 text-orange-700' },
      answered: { label: t('support.answeredStatus'), className: 'bg-green-100 text-green-700' },
      closed: { label: t('support.closedStatus'), className: 'bg-gray-100 text-gray-700' },
    }
    return configs[status] || { label: status, className: 'bg-gray-100 text-gray-700' }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  if (!inquiryId) return null

  return (
    <Dialog open={!!inquiryId} onOpenChange={(open) => { if (!open) onClose() }}>
    <DialogContent className="sm:max-w-2xl">
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
            <p className="mt-4 text-gray-600">{t('support.loading')}</p>
          </div>
        </div>
      ) : error || !inquiry ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-3 w-10 h-10 text-red-400" />
            <p className="text-gray-500">{t('support.cannotLoadInquiry')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Status & Category */}
          <div className="flex gap-2 items-center">
            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusConfig(inquiry.status).className}`}>
              {getStatusConfig(inquiry.status).label}
            </span>
            <span className="inline-flex px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
              {getCategoryLabel(inquiry.category)}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900">{inquiry.title}</h3>

          {/* Content */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-gray-700">{t('support.inquiryContent')}</h4>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {inquiry.content}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {formatDateTimeDot(inquiry.createdAt)}
            </p>
          </div>

          {/* Attachments */}
          {inquiry.attachments && inquiry.attachments.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-700">{t('support.attachments')}</h4>
              <div className="space-y-2">
                {inquiry.attachments.map((attachment) => (
                  <div
                    key={attachment.attachmentId}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex flex-1 gap-2 items-center min-w-0">
                      <FileIcon className="flex-shrink-0 w-4 h-4 text-gray-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.fileName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.fileSize)}
                        </p>
                      </div>
                    </div>
                    {attachment.downloadUrl && (
                      <a
                        href={attachment.downloadUrl}
                        download
                        className="flex-shrink-0 p-2 rounded-lg transition-colors text-primary-600 hover:bg-primary-50"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Notice */}
          {inquiry.status === 'pending' && (
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-800">
                {t('support.pendingNotice')}
              </p>
            </div>
          )}

          {/* Replies */}
          {inquiry.replies && inquiry.replies.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-700">{t('support.replies')}</h4>
              <div className="space-y-3">
                {inquiry.replies.map((reply) => (
                  <div key={reply.replyId} className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex gap-2 items-center mb-2">
                      <span className="text-sm font-semibold text-blue-900">
                        {reply.authorName}
                      </span>
                      <span className="text-xs text-blue-600">
                        ({reply.authorRole})
                      </span>
                    </div>
                    <p className="text-sm text-blue-800 whitespace-pre-wrap">
                      {reply.content}
                    </p>
                    <p className="mt-2 text-xs text-blue-600">
                      {formatDateTimeDot(reply.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reply Form */}
          {inquiry.status !== 'closed' && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-gray-700">{t('support.additionalInquiry')}</h4>
              <textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                disabled={replyMutation.isPending}
                placeholder={t('support.additionalInquiryPlaceholder')}
                rows={3}
                className="px-3 py-2 w-full rounded-lg border border-gray-300 resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleReplySubmit}
                  disabled={replyMutation.isPending || !replyContent.trim()}
                  className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {replyMutation.isPending ? (
                    <>
                      <div className="inline-block w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                      <span>{t('support.submitting')}</span>
                    </>
                  ) : (
                    <span>{t('support.submitAdditional')}</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
            >
              {t('support.close')}
            </button>
          </div>
        </div>
      )}
    </DialogContent>
    </Dialog>
  )
}

interface NewInquiryModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { title: string; content: string; category: string; files?: File[] }) => void
  isSubmitting: boolean
}

function NewInquiryModal({ isOpen, onClose, onSubmit, isSubmitting }: NewInquiryModalProps) {
  const { t } = useTranslation('learner')
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState('')
  const [files, setFiles] = useState<File[]>([])

  const MAX_FILES = 3
  const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])

    if (files.length + selectedFiles.length > MAX_FILES) {
      toast.warning(t('support.maxFilesWarning', { max: MAX_FILES }))
      return
    }

    for (const file of selectedFiles) {
      if (file.size > MAX_FILE_SIZE) {
        toast.warning(t('support.fileSizeWarning', { name: file.name }))
        return
      }
    }

    setFiles([...files, ...selectedFiles])
  }

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleSubmit = () => {
    if (!title.trim() || !content.trim() || !category) {
      toast.warning(t('support.fillAllFields'))
      return
    }
    onSubmit({ title, content, category, files: files.length > 0 ? files : undefined })
    setTitle('')
    setContent('')
    setCategory('')
    setFiles([])
  }

  const handleClose = () => {
    setTitle('')
    setContent('')
    setCategory('')
    setFiles([])
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose() }}>
    <DialogContent className="sm:max-w-2xl">
      <div className="space-y-4">
        {/* Category */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t('support.category')}
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={isSubmitting}
            className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value="">{t('support.selectCategory')}</option>
            <option value="video_error">{t('support.categoryVideoError')}</option>
            <option value="quiz_error">{t('support.categoryQuizError')}</option>
            <option value="other_error">{t('support.categoryOtherError')}</option>
            <option value="learning">{t('support.categoryLearning')}</option>
            <option value="usage">{t('support.categoryUsage')}</option>
          </select>
        </div>

        {/* Title */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t('support.inquiryTitle')}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isSubmitting}
            placeholder={t('support.inquiryTitlePlaceholder')}
            className="px-3 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Content */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t('support.inquiryContentLabel')}
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={isSubmitting}
            placeholder={t('support.inquiryContentPlaceholder')}
            rows={6}
            className="px-3 py-2 w-full rounded-lg border border-gray-300 resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            {t('support.fileUpload', { max: MAX_FILES })}
          </label>
          <div className="space-y-2">
            {files.length < MAX_FILES && (
              <label className="flex justify-center items-center px-4 py-3 w-full rounded-lg border-2 border-gray-300 border-dashed transition-colors cursor-pointer hover:border-primary-400 hover:bg-gray-50">
                <div className="flex gap-2 items-center text-sm text-gray-600">
                  <Paperclip className="w-4 h-4" />
                  <span>{t('support.selectFile')}</span>
                </div>
                <input
                  type="file"
                  onChange={handleFileChange}
                  disabled={isSubmitting}
                  accept="image/jpeg,image/png,image/gif,video/mp4,video/webm,video/quicktime,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  multiple
                />
              </label>
            )}

            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex flex-1 gap-2 items-center min-w-0">
                      <FileIcon className="flex-shrink-0 w-4 h-4 text-gray-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      disabled={isSubmitting}
                      className="flex-shrink-0 p-2 text-red-600 rounded-lg transition-colors hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 justify-end pt-4">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('support.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="inline-block w-4 h-4 rounded-full border-b-2 border-white animate-spin"></div>
                <span>{t('support.submitting')}</span>
              </>
            ) : (
              <span>{t('support.submitInquiry')}</span>
            )}
          </button>
        </div>
      </div>
    </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Announcement Detail Modal (API-backed with TiptapViewer)
// ============================================================================

interface AnnouncementDetailModalProps {
  announcementId: number | null
  onClose: () => void
}

function AnnouncementDetailModal({ announcementId, onClose }: AnnouncementDetailModalProps) {
  const { t } = useTranslation('learner')
  const queryClient = useQueryClient()

  const { data: announcement, isLoading, error } = useQuery({
    queryKey: ['announcement', announcementId],
    queryFn: () => announcementApi.getById(announcementId!),
    enabled: !!announcementId,
  })

  // Mark as read when viewing
  useEffect(() => {
    if (announcementId && announcement && announcement.isRead === false) {
      announcementApi.markAsRead(announcementId).then(() => {
        queryClient.invalidateQueries({ queryKey: ['learner-announcements'] })
        queryClient.invalidateQueries({ queryKey: ['announcements-unread-count'] })
      }).catch(console.error)
    }
  }, [announcementId, announcement, queryClient])

  const formatDate = (dateString: string) => formatDateTimeDot(dateString)

  if (!announcementId) return null

  return (
    <Dialog open={!!announcementId} onOpenChange={(open) => { if (!open) onClose() }}>
    <DialogContent className="sm:max-w-2xl">
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
            <p className="mt-4 text-gray-600">{t('support.loading')}</p>
          </div>
        </div>
      ) : error || !announcement ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <AlertCircle className="mx-auto mb-3 w-10 h-10 text-red-400" />
            <p className="text-gray-500">{t('support.cannotLoadNotice')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Title with Pin */}
          <div>
            {announcement.isPinned && (
              <span className="inline-flex items-center px-2 py-1 mb-3 text-xs font-medium text-yellow-800 bg-yellow-100 rounded">
                <Pin className="mr-1 w-3 h-3" />
                {t('support.importantNotice')}
              </span>
            )}
            <h3 className="mt-1 text-xl font-bold text-gray-900">
              {announcement.title}
            </h3>
          </div>

          {/* Meta */}
          <div className="flex gap-4 items-center text-sm text-gray-500">
            <div className="flex gap-1 items-center">
              <User className="w-4 h-4" />
              {announcement.authorName}
            </div>
            <div className="flex gap-1 items-center">
              <Calendar className="w-4 h-4" />
              {formatDate(announcement.createdAt)}
            </div>
            <div className="flex gap-1 items-center">
              <Eye className="w-4 h-4" />
              {announcement.viewCount}
            </div>
          </div>

          {/* Content */}
          <div className="pt-4 border-t border-gray-200">
            <TiptapViewer content={announcement.content} />
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
            >
              {t('support.close')}
            </button>
          </div>
        </div>
      )}
    </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Main Page Component
// ============================================================================

type TabType = 'inquiries' | 'notices'

export default function MySupport() {
  const { t } = useTranslation('learner')
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabType>('notices')
  const [isNewInquiryOpen, setIsNewInquiryOpen] = useState(false)
  const [selectedInquiryId, setSelectedInquiryId] = useState<number | null>(null)
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<number | null>(null)

  // Announcement pagination & search
  const [announcementPage, setAnnouncementPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const pageSize = 10

  // Inquiry pagination
  const [inquiryPage, setInquiryPage] = useState(0)
  const inquiryPageSize = 10

  const handleSearch = () => {
    setKeyword(searchInput)
    setAnnouncementPage(0)
  }

  const { data: announcementData, isLoading: isAnnouncementsLoading, error: announcementsError } = useQuery({
    queryKey: ['learner-announcements', announcementPage, pageSize, keyword],
    queryFn: () => announcementApi.getList({ page: announcementPage, size: pageSize, keyword: keyword || undefined }),
    enabled: activeTab === 'notices',
  })

  const { data: inquiryData, isLoading: isInquiriesLoading, error: inquiriesError } = useQuery({
    queryKey: ['my-inquiries', inquiryPage, inquiryPageSize],
    queryFn: () => inquiryApi.getList({ page: inquiryPage, size: inquiryPageSize }),
    enabled: activeTab === 'inquiries',
  })

  const createInquiryMutation = useMutation({
    mutationFn: inquiryApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-inquiries'] })
      setIsNewInquiryOpen(false)
      toast.success(t('support.inquiryCreated'))
    },
    onError: () => {
      toast.error(t('support.inquiryCreateFailed'))
    },
  })

  const announcements = announcementData?.announcements || []
  const totalPages = announcementData?.totalPages || 0

  const inquiries = inquiryData?.content || []
  const inquiryTotalPages = inquiryData?.totalPages || 0

  const formatDate = (dateString: string) => formatDateDot(dateString)

  const handleNewInquiry = (data: { title: string; content: string; category: string; files?: File[] }) => {
    createInquiryMutation.mutate(data)
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-8">
        <div className="flex gap-3 items-center mb-2">
          <Headphones className="w-8 h-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">{t('support.title')}</h1>
        </div>
        <p className="text-gray-500">{t('support.subtitle')}</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('notices')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'notices'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Bell className="w-4 h-4" />
                <span>{t('support.notices')}</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'inquiries'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <MessageSquare className="w-4 h-4" />
                <span>{t('support.inquiries')}</span>
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Notices Tab (API-backed) */}
          {activeTab === 'notices' && (
            <div>
              {/* Search */}
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder={t('support.searchPlaceholder')}
                    className="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
                >
                  {t('support.search')}
                </button>
              </div>

              {isAnnouncementsLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
                    <p className="mt-4 text-gray-600">{t('support.loading')}</p>
                  </div>
                </div>
              ) : announcementsError ? (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center">
                    <AlertCircle className="mr-2 w-5 h-5 text-red-600" />
                    <p className="text-red-800">{t('support.noticesLoadError')}</p>
                  </div>
                </div>
              ) : announcements.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  {t('support.noNotices')}
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    {announcements.map((announcement: Announcement) => (
                      <div
                        key={announcement.announcementId}
                        onClick={() => setSelectedAnnouncementId(announcement.announcementId)}
                        className={clsx(
                          'rounded-lg border p-4 cursor-pointer transition-all hover:shadow-md',
                          announcement.isPinned
                            ? 'border-yellow-300 bg-yellow-50'
                            : 'border-gray-200 hover:border-primary-200'
                        )}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="flex gap-2 items-center mb-1">
                              {announcement.isPinned && (
                                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-200 text-yellow-800 rounded">
                                  <Pin className="mr-1 w-3 h-3" />
                                  {t('support.important')}
                                </span>
                              )}
                              {announcement.isRead === false && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded">
                                  NEW
                                </span>
                              )}
                            </div>
                            <h3 className={clsx(
                              'text-lg font-semibold truncate',
                              announcement.isPinned ? 'text-yellow-900' : 'text-gray-900'
                            )}>
                              {announcement.title}
                            </h3>
                            <p className="flex gap-3 items-center mt-1 text-sm text-gray-500">
                              <span>{announcement.authorName} · {formatDate(announcement.createdAt)}</span>
                              <span className="flex gap-1 items-center">
                                <Eye className="w-3.5 h-3.5" />
                                {announcement.viewCount}
                              </span>
                            </p>
                          </div>
                          <ChevronRight className="flex-shrink-0 mt-2 w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-6 space-x-2">
                      <button
                        onClick={() => setAnnouncementPage(p => Math.max(0, p - 1))}
                        disabled={announcementPage === 0}
                        className={clsx(
                          'p-2 rounded-lg transition-colors',
                          announcementPage === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        )}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <span className="text-sm text-gray-600">
                        {announcementPage + 1} / {totalPages}
                      </span>
                      <button
                        onClick={() => setAnnouncementPage(p => Math.min(totalPages - 1, p + 1))}
                        disabled={announcementPage >= totalPages - 1}
                        className={clsx(
                          'p-2 rounded-lg transition-colors',
                          announcementPage >= totalPages - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        )}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Inquiries Tab */}
          {activeTab === 'inquiries' && (
            <div>
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setIsNewInquiryOpen(true)}
                  className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
                >
                  <Plus className="w-4 h-4" />
                  {t('support.newInquiry')}
                </button>
              </div>

              {isInquiriesLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <div className="inline-block w-8 h-8 rounded-full border-b-2 animate-spin border-primary-600"></div>
                    <p className="mt-4 text-gray-600">{t('support.loading')}</p>
                  </div>
                </div>
              ) : inquiriesError ? (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center">
                    <AlertCircle className="mr-2 w-5 h-5 text-red-600" />
                    <p className="text-red-800">{t('support.inquiriesLoadError')}</p>
                  </div>
                </div>
              ) : inquiries.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  {t('support.noInquiries')}
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {inquiries.map((inquiry) => (
                      <InquiryCard
                        key={inquiry.inquiryId}
                        inquiry={inquiry}
                        onClick={() => setSelectedInquiryId(inquiry.inquiryId)}
                      />
                    ))}
                  </div>

                  {/* Inquiry Pagination */}
                  {inquiryTotalPages > 1 && (
                    <div className="flex justify-center items-center mt-6 space-x-2">
                      <button
                        onClick={() => setInquiryPage(p => Math.max(0, p - 1))}
                        disabled={inquiryPage === 0}
                        className={clsx(
                          'p-2 rounded-lg transition-colors',
                          inquiryPage === 0
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        )}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <span className="text-sm text-gray-600">
                        {inquiryPage + 1} / {inquiryTotalPages}
                      </span>
                      <button
                        onClick={() => setInquiryPage(p => Math.min(inquiryTotalPages - 1, p + 1))}
                        disabled={inquiryPage >= inquiryTotalPages - 1}
                        className={clsx(
                          'p-2 rounded-lg transition-colors',
                          inquiryPage >= inquiryTotalPages - 1
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-100'
                        )}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <NewInquiryModal
        isOpen={isNewInquiryOpen}
        onClose={() => setIsNewInquiryOpen(false)}
        onSubmit={handleNewInquiry}
        isSubmitting={createInquiryMutation.isPending}
      />

      <InquiryDetailModal
        inquiryId={selectedInquiryId}
        onClose={() => setSelectedInquiryId(null)}
      />

      <AnnouncementDetailModal
        announcementId={selectedAnnouncementId}
        onClose={() => setSelectedAnnouncementId(null)}
      />
    </div>
  )
}
